import { Client, IMessage } from '@stomp/stompjs';

import { EOrderType } from '@aqx-core/common/api/order';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum ETradeMessageType {
  RISK_POSITION = 'RISK_POSITION',
  CASH_POSITION = 'CASH_POSITION',
  TRADING_POWER = 'TRADING_POWER'
}

export interface ITradeRiskPositionMessageData {
  S: string; // symbol
  LS: number; // lotSize
  CMD: EOrderType; // orderType
  ABP: number; // averageBuyPrice
  ASP: number; // averageSoldPrice
  LP: number; // lastPrice
  MV: number; // marketValue
  PNL: number; // pnlGross
}

export interface ITradeCashPositionMessageData {
  ID: string; // accountId
  A: number; // amount
  CUR: string; // currency
}

export interface ITradeTradingPowerMessageData {
  ID: string; // accountId
  BP: number; // averageBuyPrice
  SP: string; // averageSoldPrice
}

export interface ITradeRiskPositionMessage {
  M: ETradeMessageType.RISK_POSITION;
  D: ITradeRiskPositionMessageData;
}

export interface ITradeCashPositionMessage {
  M: ETradeMessageType.CASH_POSITION;
  D: ITradeCashPositionMessageData;
}

export interface ITradeTradingPowerMessage {
  M: ETradeMessageType.TRADING_POWER;
  D: ITradeTradingPowerMessageData;
}

export type TTradeTopicMessage = ITradeRiskPositionMessage | ITradeCashPositionMessage | ITradeTradingPowerMessage;

export interface ITradeReceiver {
  (payload: TTradeTopicMessage | undefined): void;
}

interface ISubscribeParams {
  receiver: ITradeReceiver;
}

interface IProps {
  client: Client;
}

class TradeTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<ITradeReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (tradeSubscription: string, params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(tradeSubscription);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (tradeSubscription: string, params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(tradeSubscription);

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as TTradeTopicMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = (tradeSubscription: string) => {
    const subPath = `trade/${tradeSubscription}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default TradeTopic;
